/**
 * WizardShell - Shell generico para wizards modulares.
 * Renderiza o Stepper, controla navegacao e carrega steps dinamicamente.
 *
 * Expansao dinamica:
 * - PlaceBindingStep: expandido em N steps (1 por local selecionado)
 * - ScheduleStep: expandido em N x M steps (locais x profissionais selecionados)
 */

import { useState, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Stepper, Step, StepLabel, Typography,
  IconButton, Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useThemeCustom } from '../../hooks/useThemeCustom';
import useEntityTree from '../../hooks/useEntityTree';

// Import steps
import EntityPickerStep from './steps/EntityPickerStep';
import PlaceBindingStep from './steps/PlaceBindingStep';
import ScheduleStep from './steps/ScheduleStep';
import ReviewStep from './steps/ReviewStep';
import ProcessingStep from './steps/ProcessingStep';

/** Mapa de nomes de componentes para componentes reais */
const STEP_COMPONENTS = {
  EntityPickerStep,
  PlaceBindingStep,
  ScheduleStep,
  ReviewStep,
  ProcessingStep,
};

export default function WizardShell({
  open,
  onClose,
  onSuccess,
  config,
  existingProfessionals = [],
  existingServices = [],
  existingPlaces = [],
  existingSchedules = [],
  existingLinks = {},
}) {
  const { getLabel } = useThemeCustom();
  const queryClient = useQueryClient();
  const entityTree = useEntityTree({ persist: true });
  const [activeStep, setActiveStep] = useState(0);
  const [stepValid, setStepValid] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  // Expandir PlaceBindingStep e ScheduleStep em steps dinamicos
  const steps = useMemo(() => {
    const result = [];
    for (const step of config?.steps || []) {
      if (step.component === 'PlaceBindingStep') {
        const selectedPlaces = entityTree.tree.places.filter(p => p.selected);
        if (selectedPlaces.length === 0) {
          result.push(step);
        } else {
          for (const place of selectedPlaces) {
            result.push({
              ...step,
              label: selectedPlaces.length === 1
                ? 'Quem faz o que?'
                : `Em ${place.name}...`,
              props: { ...(step.props || {}), filterPlaceId: place.id },
            });
          }
        }
      } else if (step.component === 'ScheduleStep') {
        const selectedPlaces = entityTree.tree.places.filter(p => p.selected);
        const selectedProfs = entityTree.tree.professionals.filter(p => p.selected);
        if (selectedPlaces.length === 0 || selectedProfs.length === 0) {
          result.push(step);
        } else {
          for (const place of selectedPlaces) {
            for (const prof of selectedProfs) {
              result.push({
                ...step,
                label: `Agenda de ${prof.name} em ${place.name}`,
                props: {
                  ...(step.props || {}),
                  filterPlaceId: place.id,
                  filterProfessionalId: prof.id,
                },
              });
            }
          }
        }
      } else {
        result.push(step);
      }
    }
    return result;
  }, [config?.steps, entityTree.tree.places, entityTree.tree.professionals]);

  // Labels do stepper (exclui o ultimo step "Criacao" do stepper visual)
  const stepLabels = useMemo(() =>
    steps.slice(0, -1).map(s => s.label),
    [steps]
  );

  // Reset e carrega dados ao abrir o dialog
  const handleDialogEnter = useCallback(() => {
    console.log('[WizardShell] Dialog aberto - config:', config?.id, {
      professionals: existingProfessionals?.length,
      services: existingServices?.length,
      places: existingPlaces?.length,
      links: {
        placeProfessionals: existingLinks?.placeProfessionals?.length || 0,
        placeServices: existingLinks?.placeServices?.length || 0,
        placeServiceProfessionals: existingLinks?.placeServiceProfessionals?.length || 0,
      },
    });
    setActiveStep(0);
    setStepValid(false);
    setIsProcessing(false);
    setIsComplete(false);
    entityTree.reset();
    entityTree.loadExisting(existingProfessionals, existingServices, existingPlaces, existingLinks);
  }, [config, existingProfessionals, existingServices, existingPlaces, existingLinks]);

  // Navegacao
  const handleNext = useCallback(() => {
    if (activeStep < steps.length - 1) {
      setActiveStep(prev => prev + 1);
      setStepValid(false);
    }
  }, [activeStep, steps.length]);

  const handleBack = useCallback(() => {
    if (activeStep > 0) {
      setActiveStep(prev => prev - 1);
      setStepValid(true); // Ao voltar, assume valido (ja preencheu)
    }
  }, [activeStep]);

  const handleClose = useCallback(() => {
    if (isProcessing) return;
    onClose();
  }, [isProcessing, onClose]);

  const handleFinish = useCallback(() => {
    entityTree.reset();
    // Invalida todas as entidades que o wizard pode ter criado
    queryClient.invalidateQueries({ queryKey: ['schedules'] });
    queryClient.invalidateQueries({ queryKey: ['professionals'] });
    queryClient.invalidateQueries({ queryKey: ['places'] });
    queryClient.invalidateQueries({ queryKey: ['services'] });
    onClose();
    if (onSuccess) onSuccess();
  }, [onClose, onSuccess, entityTree, queryClient]);

  // Renderiza o step atual
  const renderCurrentStep = () => {
    const stepConfig = steps[activeStep];
    if (!stepConfig) return null;

    const StepComponent = STEP_COMPONENTS[stepConfig.component];
    if (!StepComponent) {
      return (
        <Typography color="error">
          Step component "{stepConfig.component}" nao encontrado.
        </Typography>
      );
    }

    return (
      <StepComponent
        key={`${activeStep}_${stepConfig.props?.filterPlaceId || ''}_${stepConfig.props?.filterProfessionalId || ''}`}
        entityTree={entityTree}
        mode={stepConfig.mode || 'select-or-create'}
        config={stepConfig}
        stepProps={stepConfig.props || {}}
        getLabel={getLabel}
        existingSchedules={existingSchedules}
        onValidChange={setStepValid}
        onNext={handleNext}
        onProcessingStart={() => setIsProcessing(true)}
        onProcessingEnd={() => setIsProcessing(false)}
        onComplete={() => setIsComplete(true)}
        onFinish={handleFinish}
      />
    );
  };

  // Determina se o step atual e o de resumo, processamento, etc.
  const currentStepConfig = steps[activeStep];
  const isProcessingStep = currentStepConfig?.component === 'ProcessingStep';
  const isReviewStep = currentStepConfig?.component === 'ReviewStep';
  const showStepper = !isProcessingStep;
  const showBackButton = activeStep > 0 && !isProcessingStep && !isProcessing;
  const showNextButton = activeStep < steps.length - 1 && !isProcessingStep && !isReviewStep;
  const showCreateButton = isReviewStep;
  const showCancelButton = !isProcessing && !isComplete;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{ onEnter: handleDialogEnter }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="span">
          {config?.name || 'Assistente'}
        </Typography>
        {!isProcessing && (
          <Tooltip title="Fechar">
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {/* Stepper */}
        {showStepper && (
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {stepLabels.map((label, index) => (
              <Step key={index} completed={index < activeStep}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {/* Step content */}
        {renderCurrentStep()}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {/* Voltar */}
        {showBackButton && (
          <Button onClick={handleBack} startIcon={<ArrowBackIcon />}>
            Voltar
          </Button>
        )}

        <Box sx={{ flex: 1 }} />

        {/* Cancelar */}
        {showCancelButton && !isComplete && (
          <Button onClick={handleClose} color="inherit">
            Cancelar
          </Button>
        )}

        {/* Proximo */}
        {showNextButton && (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!stepValid}
            endIcon={<ArrowForwardIcon />}
          >
            Continuar
          </Button>
        )}

        {/* Criar (no step de resumo) */}
        {showCreateButton && (
          <Button
            variant="contained"
            color="success"
            onClick={handleNext}
          >
            Criar Tudo
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
